import { inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState, signalStore, withHooks, withMethods, withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  AppError, setError, setPending, setSuccess, withFiltersUpdate, withRequestHandler,
  withRequestStatus,
} from '@hglobal/common';
import { UserInfoAdmin } from '@pt/models';
import { filter, pipe, switchMap, tap } from 'rxjs';

import { COURSES_REPORT_API } from './api';
import { CoursesReportFilters } from './models';
import { MemberCourse } from './models/member-course';

interface CoursesReportState {
  filters: CoursesReportFilters;
  itemsCount: number;
  memberCourses: MemberCourse[];
  userInfo: UserInfoAdmin;
}

export const initialFilters: CoursesReportFilters = {
  page: 1,
  limit: 10,
  search: null,
  courseType: null,
  courseStatus: null,
  userId: null,
};

export const initialState: CoursesReportState = {
  filters: initialFilters,
  itemsCount: null,
  memberCourses: [],
  userInfo: null,
};

export const CoursesReportStore = signalStore(
  withState<CoursesReportState>(initialState),
  withRequestStatus(),
  withRequestHandler(),
  withFiltersUpdate(),
  withMethods(
    store => {
      const apiService = inject(COURSES_REPORT_API);

      return {
        getUserInfo: rxMethod<number>(
          pipe(
            tap(() => patchState(store, setPending())),
            switchMap(
              userId => apiService.getUserInfo(userId)
                .pipe(
                  tapResponse({
                    next: userInfo => {
                      patchState(
                        store,
                        {
                          userInfo,
                          filters: { ...store.filters(), userId: userInfo.id },
                        },
                      );
                    },
                    error: (error: AppError) => patchState(store, setError(error)),
                  }),
                ),
            ),
          ),
        ),
        getMemberCourses: rxMethod<CoursesReportFilters>(
          pipe(
            filter(params => !!params.userId),
            tap(() => patchState(store, setPending())),
            switchMap(
              filters => apiService.getMemberCourses(filters)
                .pipe(
                  tapResponse({
                    next: ({ itemsCount, memberCourses }) => {
                      patchState(store, { itemsCount, memberCourses }, setSuccess());
                    },
                    error: (error: AppError) => patchState(store, setError(error)),
                  }),
                ),
            ),
          ),
        ),
      };
    },
  ),
  withHooks({
    onInit(store) {
      store.getMemberCourses(store.filters);
    },
  }),
);
